import { useEffect, useId, useRef, useState } from 'react';
import { theme } from 'antd';
import type { AdminUsageDailyPoint } from '../../api/usage';
import { formatCreditsText } from './format';

function axisLabel(value: number): string {
  if (value >= 1e15) return value.toExponential(1);
  if (value >= 1000) return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  return formatCreditsText(value.toFixed(6));
}

export function CreditsTrend({ data }: { data: AdminUsageDailyPoint[] }) {
  const { token } = theme.useToken();
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(740);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const tooltipId = useId();
  useEffect(() => setActiveIndex(null), [data]);
  useEffect(() => {
    if (!container.current) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, entry.contentRect.width)));
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  const max = Math.max(...data.map((point) => Number(point.creditsUsed)), 0.01);
  const points = data.map((point, index) => ({
    ...point,
    x: data.length === 1 ? (width + 36) / 2 : 60 + index * (width - 84) / (data.length - 1),
    y: 220 - Number(point.creditsUsed) / max * 180,
  }));
  const tickStep = Math.max(1, Math.ceil((points.length - 1) / (width < 500 ? 2 : 4)));
  // Horizontal handles keep each smooth segment within its adjacent daily values.
  const curvePath = points.map((point, index) => {
    if (index === 0) return `M ${point.x},${point.y}`;
    const previous = points[index - 1];
    const middleX = (previous.x + point.x) / 2;
    return `C ${middleX},${previous.y} ${middleX},${point.y} ${point.x},${point.y}`;
  }).join(' ');
  const active = activeIndex == null ? undefined : points[activeIndex];
  return (
    <div ref={container} style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${width} 270`} style={{ width: '100%', height: 270, display: 'block' }} role="img" aria-label="每日消耗积分折线图"
        onMouseMove={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect();
          const x = (event.clientX - bounds.left) * width / bounds.width;
          const y = (event.clientY - bounds.top) * 270 / bounds.height;
          if (!points.length || x < 60 || x > width - 24 || y < 40 || y > 220) {
            setActiveIndex(null);
            return;
          }
          setActiveIndex(Math.round((x - 60) / (width - 84) * (points.length - 1)));
        }}
        onMouseLeave={() => setActiveIndex(null)}>
        <rect x={60} y={40} width={width - 84} height={180} fill="transparent" />
        {[0, 0.5, 1].map((part) => (
          <g key={part}>
            <line x1={60} x2={width - 24} y1={220 - part * 180} y2={220 - part * 180} stroke="#e5e7eb" />
            <text x={50} y={225 - part * 180} textAnchor="end" fontSize={12} fill="#666">{axisLabel(max * part)}</text>
          </g>
        ))}
        <path d={curvePath} fill="none" stroke={token.colorPrimary} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
        {active && <line x1={active.x} x2={active.x} y1={40} y2={220} stroke={token.colorPrimaryBorder} strokeDasharray="4 4" pointerEvents="none" />}
        {points.map((point, index) => (
          <g key={point.date}>
            <circle cx={point.x} cy={point.y} r={activeIndex === index || data.length === 1 ? 5 : 3}
              fill={activeIndex === index ? token.colorPrimary : token.colorBgContainer}
              stroke={token.colorPrimary} strokeWidth={1.5} tabIndex={0}
              aria-label={`${point.date} ${formatCreditsText(point.creditsUsed)} 积分`}
              aria-describedby={activeIndex === index ? tooltipId : undefined}
              onFocus={() => setActiveIndex(index)} onBlur={() => setActiveIndex(null)} />
            {(index % tickStep === 0 || index === points.length - 1) && (
              <text x={point.x} y={250} textAnchor="middle" fontSize={12} fill="#666">{point.date.slice(5)}</text>
            )}
          </g>
        ))}
      </svg>
      {active && (
        <div role="tooltip" id={tooltipId} style={{
          position: 'absolute', pointerEvents: 'none', zIndex: 1,
          left: Math.min(Math.max(active.x - 100, 0), Math.max(width - 200, 0)),
          top: active.y < 100 ? active.y + 16 : active.y - 72,
          width: 200, boxSizing: 'border-box', padding: '8px 12px',
          background: '#fff', border: '1px solid #d9d9d9', borderRadius: 4,
          boxShadow: '0 2px 8px #0000001a', fontSize: 12, overflowWrap: 'anywhere',
        }}>
          <div style={{ color: '#666', marginBottom: 4 }}>{active.date}</div>
          <strong>{formatCreditsText(active.creditsUsed)} 积分</strong>
        </div>
      )}
    </div>
  );
}
